import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  MapIcon,
  Square2StackIcon,
  MagnifyingGlassIcon,
  PhoneArrowUpRightIcon,
  ListBulletIcon,
  UsersIcon,
  EyeIcon,
  HeartIcon,
  GlobeAltIcon,
  UserCircleIcon,
  BookmarkIcon,
  ClockIcon,
  MegaphoneIcon,
  TagIcon,
  CursorArrowRaysIcon,
  RectangleStackIcon,
  RectangleGroupIcon,
  SparklesIcon,
  CalendarDaysIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { classNames } from '../../../utilities/classNames';
import client from '../../../lib/feathers';
import Page from '../../Page';
import BasicStatsView from './BasicStatsView';
import AdvancedAnalyticsView from './AdvancedAnalyticsView';
import AnalyticsUpgradePrompt from './AnalyticsUpgradePrompt';

const statLegend = [
  { name: 'Views', key: 'load', icon: EyeIcon },
  { name: 'Searches', key: 'search', icon: MagnifyingGlassIcon },
  { name: 'Listing Nearby', key: 'nearby', icon: Square2StackIcon },
  { name: 'Discover Nearby', key: 'discover', icon: Square2StackIcon },
  { name: 'Check-Ins', key: 'checkin', icon: UsersIcon },
  { name: 'Sips', key: 'sip', icon: HeartIcon },
  { name: 'Lists Added To', key: 'list', icon: ListBulletIcon },
  { name: 'Directions', key: 'directions', icon: MapIcon },
  { name: 'Phone Calls', key: 'call', icon: PhoneArrowUpRightIcon },
  { name: 'Hours', key: 'hours', icon: ClockIcon },
  { name: 'Listing Saves', key: 'save', icon: BookmarkIcon },
  { name: 'Website Clicks', key: 'website', icon: GlobeAltIcon },
  { name: 'Social Media Clicks', key: 'social', icon: UserCircleIcon },

  // What the winery itself publishes. Separate from the discovery metrics
  // above because these measure whether the effort a winery puts into its
  // own listing is read, rather than whether people find it.
  { name: 'Welcome Message Seen', key: 'welcome_message_view', icon: MegaphoneIcon },
  { name: 'Promotions Opened', key: 'promotion_view', icon: TagIcon },
  { name: 'Promotion Clicks', key: 'promotion_click', icon: CursorArrowRaysIcon },
  { name: 'Wine List Views', key: 'libation_list_view', icon: RectangleStackIcon },
  { name: 'Wine Views', key: 'libation_view', icon: SparklesIcon },
  { name: 'Vintage Views', key: 'libation_vintage_view', icon: CalendarDaysIcon },
  { name: 'Flight List Views', key: 'flight_list_view', icon: RectangleGroupIcon },
  { name: 'Flight Views', key: 'flight_view', icon: RectangleGroupIcon },
  // Somebody pointed a camera at a printed flight list in the tasting room.
  // 46 of these are recorded and none were shown, because this legend is what
  // the report iterates: an action with no row here is dropped whatever the
  // data says. It is the only one of the 24 that was missing.
  { name: 'Flight List Scans', key: 'flight-list-scan', icon: RectangleStackIcon },

  // The two that end in money. Both read zero today and will keep reading
  // zero until the links exist: contact.reservationUrl is set on 2 of 4,428
  // listings, and libationVintage.purchaseUrl on 0 of 48,595 vintages with
  // nothing in the client rendering it. Shown anyway, because a visible zero
  // is the argument for fixing that.
  { name: 'Reservation Clicks', key: 'reservation', icon: ClockIcon },
  { name: 'Bottle Purchase Clicks', key: 'libation_vintage_purchase', icon: ShoppingCartIcon },
];

export default function ReportsPanel() {
  const { lid } = useParams();
  const user = useSelector((s) => s.auth.user);

  const [view, setView] = useState('basic');
  const [stats, setStats] = useState([]);
  const [advancedData, setAdvancedData] = useState(null);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [period, setPeriod] = useState('30d');

  // Load basic stats when basic view is active
  useEffect(() => {
    if (view !== 'basic') return;
    const loadStats = async () => {
      const service = client.service(`${user.role}/listing-basic-stats`);
      const response = await service.get(lid);
      const countFor = (bucket, key) => response[bucket].find((d) => d.action === key)?.count || 0;
      const build = (key, name, icon) => ({
        name,
        icon,
        key,
        currentStat: countFor('current', key),
        previousStat: countFor('previous', key),
        totalStat: countFor('totals', key),
      });

      const _stats = statLegend.map((stat) => build(stat.key, stat.name, stat.icon));

      // Anything the server counted that this legend does not name is shown
      // anyway, under a readable version of its own key.
      //
      // The report iterates the LEGEND, not the data, so an action with no row
      // here was dropped however much of it there was. `flight-list-scan` sat
      // at 46 recorded and 0 displayed for exactly that reason, and the ten
      // actions added this month would each have done the same.
      //
      // Same call as an unnamed modal in the tracking code: being counted is
      // the default and the nice label is the refinement. A plain label is
      // visible and invites someone to improve it; a missing row is not.
      const named = new Set(statLegend.map((stat) => stat.key));
      const extras = [...new Set(response.totals.map((d) => d.action))]
        .filter((key) => key && !named.has(key))
        .map((key) => build(key, key.replace(/[-_]/g, ' ').replace(/^./, (c) => c.toUpperCase()), CursorArrowRaysIcon));

      setStats([..._stats, ...extras]);
    };
    loadStats();
  }, [lid, view, user.role]);

  // Load advanced analytics when advanced view is active or period changes
  useEffect(() => {
    if (view !== 'advanced') return;
    const loadAdvanced = async () => {
      setAdvancedLoading(true);
      try {
        const service = client.service(`${user.role}/listing-analytics`);
        const response = await service.get(lid, { query: { period, includeRegionalBenchmark: 'true' } });
        if (response.hasAccess === false) {
          setHasAccess(false);
          setAdvancedData(null);
        } else {
          setHasAccess(true);
          setAdvancedData(response);
        }
      } catch (err) {
        console.error('Failed to load advanced analytics:', err);
        setHasAccess(false);
      } finally {
        setAdvancedLoading(false);
      }
    };
    loadAdvanced();
  }, [lid, view, period, user.role]);

  return (
    <Page>
      <div className="bg-white shadow overflow-hidden px-5 py-5 sm:rounded-lg sm:p-6">
        {/* Header */}
        <div className="sm:flex sm:items-center py-2">
          <div className="sm:flex-auto py-2">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Reports</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Analytics, overall impressions, and weekly trends for your listing.
            </p>
          </div>
          <div className="mt-3 sm:mt-0 flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('basic')}
              className={classNames(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                view === 'basic'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Basic Stats
            </button>
            <button
              onClick={() => setView('advanced')}
              className={classNames(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                view === 'advanced'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Advanced Analytics
            </button>
          </div>
        </div>

        {/* Basic Stats View */}
        {view === 'basic' && <BasicStatsView stats={stats} />}

        {/* Advanced Analytics View */}
        {view === 'advanced' && (
          <>
            {advancedLoading && (
              <div className="py-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
                <p className="mt-3 text-sm text-gray-500">Loading analytics...</p>
              </div>
            )}
            {!advancedLoading && hasAccess === false && <AnalyticsUpgradePrompt />}
            {!advancedLoading && hasAccess && advancedData && (
              <AdvancedAnalyticsView
                data={advancedData}
                period={period}
                onPeriodChange={setPeriod}
              />
            )}
          </>
        )}
      </div>
    </Page>
  );
}
